// NOTICE: KAPTION-INTEGRATION
// Composable for managing scheduled messages at account level (across all conversations)
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import KaptionScheduledMessagesAPI from 'dashboard/api/kaptionScheduledMessages';

/**
 * Composable for managing scheduled messages at account level
 * Shows all scheduled messages across all conversations with search support
 *
 * @returns {Object} State and methods for account-level scheduled messages
 */
export function useKaptionAccountScheduledMessages() {
  const store = useStore();
  const scheduledMessages = ref([]);
  const scheduledMessagesCount = ref(0);
  const isFetching = ref(false);
  const isCancelling = ref(false);
  const searchQuery = ref('');

  let debounceTimer = null;

  const accountId = computed(() => store.getters.getCurrentAccountId);

  const fetchScheduledMessages = async () => {
    if (!accountId.value) {
      scheduledMessages.value = [];
      return;
    }

    isFetching.value = true;
    try {
      const params = {};
      if (searchQuery.value) {
        params.q = searchQuery.value;
      }
      const response =
        await KaptionScheduledMessagesAPI.getAccountScheduledMessages(
          accountId.value,
          params
        );
      scheduledMessages.value = response.data.scheduled_messages || [];
    } catch {
      scheduledMessages.value = [];
    } finally {
      isFetching.value = false;
    }
  };

  const fetchCount = async () => {
    if (!accountId.value) {
      scheduledMessagesCount.value = 0;
      return;
    }

    try {
      const response =
        await KaptionScheduledMessagesAPI.getAccountScheduledMessagesCount(
          accountId.value
        );
      scheduledMessagesCount.value = response.data.count || 0;
    } catch {
      scheduledMessagesCount.value = 0;
    }
  };

  const cancelScheduledMessage = async (conversationId, messageId) => {
    if (!accountId.value || !conversationId || !messageId) return;

    isCancelling.value = true;
    try {
      await KaptionScheduledMessagesAPI.cancelScheduledMessage(
        accountId.value,
        conversationId,
        messageId
      );
      await fetchScheduledMessages();
      await fetchCount();
    } finally {
      isCancelling.value = false;
    }
  };

  const setSearchQuery = query => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      searchQuery.value = query;
    }, 300);
  };

  // Watch for search changes to re-fetch
  watch(searchQuery, () => {
    fetchScheduledMessages();
  });

  // Watch for account changes
  watch(accountId, () => {
    fetchScheduledMessages();
    fetchCount();
  });

  // Listen for 'kaption-message-scheduled' event to refresh
  const handleMessageScheduled = () => {
    fetchScheduledMessages();
    fetchCount();
  };

  onMounted(() => {
    emitter.on('kaption-message-scheduled', handleMessageScheduled);
    fetchCount();
  });

  onUnmounted(() => {
    emitter.off('kaption-message-scheduled', handleMessageScheduled);
    if (debounceTimer) clearTimeout(debounceTimer);
  });

  return {
    scheduledMessages,
    scheduledMessagesCount,
    isFetching,
    isCancelling,
    searchQuery,
    refresh: fetchScheduledMessages,
    fetchCount,
    cancelScheduledMessage,
    setSearchQuery,
  };
}
